import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { ChevronRight, ChevronLeft, Check, Calculator, Building, Users, FileText, Home, User, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Constants
const BASE_COST = 5750;
const TOTAL_STEPS = 8;

const CostCalculator = () => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [estimatedCost, setEstimatedCost] = useState(BASE_COST);
  const [formData, setFormData] = useState({
    businessActivity: '',
    jurisdiction: '',
    sponsorship: '',
    owners: '',
    visas: '',
    officeSpace: '',
    businessName: '',
    name: '',
    email: '',
    phone: '',
    nationality: '',
  });

  const totalSteps = TOTAL_STEPS;
  const progress = (step / totalSteps) * 100;

  const businessActivities = [
    'IT Consultancy', 'E-Commerce', 'General Trading', 'Manufacturing',
    'Business Consultancy', 'Marketing Services', 'Real Estate',
    'Food & Beverage', 'Import/Export', 'Other'
  ];

  const jurisdictionOptions = [
    { name: 'Dubai Mainland', icon: Building },
    { name: 'Free Zone', icon: Users },
    { name: 'Offshore Company', icon: User },
    { name: 'Saudi Arabia (KSA)', icon: FileText },
  ];

  const handleNext = () => {
    if (step < totalSteps) setStep(step + 1);
  };

  const handlePrev = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const payload = {
        business_activity: formData.businessActivity,
        jurisdiction: formData.jurisdiction,
        owners: formData.owners,
        visas: formData.visas,
        office_space: formData.officeSpace,
        business_name: formData.businessName || '',
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
      };

      const response = await fetch(`${API_URL}/api/calculator`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      const data = await response.json();
      setEstimatedCost(data.estimated_cost);

      toast({
        title: "Request Submitted Successfully!",
        description: "We will send you a detailed quote shortly.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  return (
    <section id="cost-calculator" data-testid="cost-calculator-section" className="py-20 bg-gradient-to-br from-[#0A1929] via-[#1a1033] to-[#0A1929] text-white relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-20 w-64 h-64 bg-purple-500 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-pink-500 rounded-full blur-3xl animate-pulse delay-700"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <Calculator size={48} className="text-[#DC143C]" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Calculate Your <span className="text-[#DC143C]">Business Setup Cost</span> Now
          </h2>
          <p className="text-xl text-gray-300">Get an instant quote tailored to your needs</p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between mb-2">
              <span className="text-sm font-medium">Step {step} of {totalSteps}</span>
              <span className="text-sm font-medium">{Math.round(progress)}% Complete</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-[#6B1B4D] to-[#DC143C] h-full rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              >
                <div className="h-full w-full bg-white opacity-20 animate-pulse"></div>
              </div>
            </div>
          </div>

          <Card className="bg-white/10 backdrop-blur-lg border-white/20 p-8 md:p-12 rounded-2xl shadow-2xl text-white">
            {/* Step 1: Business Activity */}
            {step === 1 && (
              <div className="space-y-6 animate-in fade-in slide-in-from-right duration-500">
                <h3 className="text-2xl font-bold mb-6">Which Business Activity are you looking for?</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {businessActivities.map((activity, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        updateFormData('businessActivity', activity);
                        setTimeout(handleNext, 300);
                      }}
                      className={`p-4 rounded-lg border-2 transition-all hover:scale-105 text-white ${
                        formData.businessActivity === activity
                          ? 'border-[#DC143C] bg-[#DC143C]/20'
                          : 'border-white/30 hover:border-white/50 bg-white/5'
                      }`}
                    >
                      {activity}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Jurisdiction */}
            {step === 2 && (
              <div className="space-y-6 animate-in fade-in slide-in-from-right duration-500">
                <h3 className="text-2xl font-bold mb-6">Select Business Structure</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {jurisdictionOptions.map((option, idx) => {
                    const IconComponent = option.icon;
                    return (
                      <button
                        key={idx}
                        onClick={() => {
                          updateFormData('jurisdiction', option.name);
                          setTimeout(handleNext, 300);
                        }}
                        className={`p-6 rounded-xl border-2 transition-all hover:scale-105 flex items-center gap-4 text-white ${
                          formData.jurisdiction === option.name
                            ? 'border-[#DC143C] bg-[#DC143C]/20'
                            : 'border-white/30 hover:border-white/50 bg-white/5'
                        }`}
                      >
                        <IconComponent size={32} />
                        <span className="text-left font-medium">{option.name}</span>
                        {formData.jurisdiction === option.name && <Check className="ml-auto" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 3: Number of Owners */}
            {step === 3 && (
              <div className="space-y-6 animate-in fade-in slide-in-from-right duration-500">
                <h3 className="text-2xl font-bold mb-6">How Many Owners will be involved?</h3>
                <div className="grid grid-cols-3 gap-4">
                  {['Single', 'Multiple (2-5)', 'Not Sure'].map((option, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        updateFormData('owners', option);
                        setTimeout(handleNext, 300);
                      }}
                      className={`p-6 rounded-xl border-2 transition-all hover:scale-105 text-white ${
                        formData.owners === option
                          ? 'border-[#DC143C] bg-[#DC143C]/20'
                          : 'border-white/30 hover:border-white/50 bg-white/5'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 4: Visas Required */}
            {step === 4 && (
              <div className="space-y-6 animate-in fade-in slide-in-from-right duration-500">
                <h3 className="text-2xl font-bold mb-6">How many visas are required?</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {['1-2', '3-5', '6-10', '10+'].map((option, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        updateFormData('visas', option);
                        setTimeout(handleNext, 300);
                      }}
                      className={`p-6 rounded-xl border-2 transition-all hover:scale-105 text-white ${
                        formData.visas === option
                          ? 'border-[#DC143C] bg-[#DC143C]/20'
                          : 'border-white/30 hover:border-white/50 bg-white/5'
                      }`}
                    >
                      <User className="mx-auto mb-2" size={32} />
                      <span className="text-xl font-bold">{option}</span>
                    </button>
                  ))}
                </div>
                <p className="text-sm text-gray-300 text-center mt-4">Select the approximate number of visas you'll need for employees and partners</p>
              </div>
            )}

            {/* Step 5: Office Space */}
            {step === 5 && (
              <div className="space-y-6 animate-in fade-in slide-in-from-right duration-500">
                <h3 className="text-2xl font-bold mb-6">Do you require an office space?</h3>
                <div className="grid grid-cols-3 gap-4">
                  {['Yes', 'No', 'Not Sure'].map((option, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        updateFormData('officeSpace', option);
                        setTimeout(handleNext, 300);
                      }}
                      className={`p-6 rounded-xl border-2 transition-all hover:scale-105 flex flex-col items-center gap-3 text-white ${
                        formData.officeSpace === option
                          ? 'border-[#DC143C] bg-[#DC143C]/20'
                          : 'border-white/30 hover:border-white/50 bg-white/5'
                      }`}
                    >
                      <Home size={32} />
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 6: Business Name */}
            {step === 6 && (
              <div className="space-y-6 animate-in fade-in slide-in-from-right duration-500">
                <h3 className="text-2xl font-bold mb-6">Do you have a business name in mind?</h3>
                <Input
                  type="text"
                  placeholder="Enter your business name"
                  value={formData.businessName}
                  onChange={(e) => updateFormData('businessName', e.target.value)}
                  className="bg-white/10 border-white/30 text-white placeholder:text-gray-400 h-14 text-lg"
                />
                <p className="text-sm text-gray-300">Don't worry if you're not sure yet. We can help you with name selection!</p>
              </div>
            )}

            {/* Step 7: Contact Details */}
            {step === 7 && (
              <div className="space-y-6 animate-in fade-in slide-in-from-right duration-500">
                <h3 className="text-2xl font-bold mb-6">Contact Details</h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-white mb-2 block">Full Name *</Label>
                    <Input
                      type="text"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={(e) => updateFormData('name', e.target.value)}
                      className="bg-white/10 border-white/30 text-white placeholder:text-gray-400 h-12"
                    />
                  </div>
                  <div>
                    <Label className="text-white mb-2 block">Email *</Label>
                    <Input
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={(e) => updateFormData('email', e.target.value)}
                      className="bg-white/10 border-white/30 text-white placeholder:text-gray-400 h-12"
                    />
                  </div>
                  <div>
                    <Label className="text-white mb-2 block">Phone Number *</Label>
                    <Input
                      type="tel"
                      placeholder="+971 52 866 2076"
                      value={formData.phone}
                      onChange={(e) => updateFormData('phone', e.target.value)}
                      className="bg-white/10 border-white/30 text-white placeholder:text-gray-400 h-12"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 8: Review & Submit */}
            {step === 8 && (
              <div className="space-y-6 animate-in fade-in slide-in-from-right duration-500">
                <h3 className="text-2xl font-bold mb-6">Review Your Requirements</h3>
                <div className="bg-white/5 rounded-xl p-6 space-y-3">
                  <div className="flex justify-between py-2 border-b border-white/10">
                    <span className="text-gray-300">Business Activity:</span>
                    <span className="font-medium text-white">{formData.businessActivity}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-white/10">
                    <span className="text-gray-300">Business Structure:</span>
                    <span className="font-medium text-white">{formData.jurisdiction}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-white/10">
                    <span className="text-gray-300">Owners:</span>
                    <span className="font-medium text-white">{formData.owners}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-white/10">
                    <span className="text-gray-300">Visas Required:</span>
                    <span className="font-medium text-white">{formData.visas}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-white/10">
                    <span className="text-gray-300">Office Space:</span>
                    <span className="font-medium text-white">{formData.officeSpace}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-white/10">
                    <span className="text-gray-300">Business Name:</span>
                    <span className="font-medium text-white">{formData.businessName || 'Not specified'}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-300">Contact:</span>
                    <span className="font-medium text-white">{formData.name}</span>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-[#6B1B4D] to-[#DC143C] rounded-xl p-6 text-center text-white">
                  <p className="text-3xl font-bold mb-2">Estimated Starting Cost</p>
                  <p className="text-5xl font-bold">AED {estimatedCost.toLocaleString()}</p>
                  <p className="text-sm mt-2 opacity-80">*Final cost may vary based on specific requirements</p>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              <Button
                onClick={handlePrev}
                disabled={step === 1}
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 disabled:opacity-50"
              >
                <ChevronLeft className="mr-2" size={20} />
                Previous
              </Button>

              {step < totalSteps ? (
                <Button
                  onClick={handleNext}
                  className="bg-gradient-to-r from-[#6B1B4D] to-[#DC143C] hover:shadow-lg"
                >
                  Next
                  <ChevronRight className="ml-2" size={20} />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="bg-gradient-to-r from-green-500 to-green-600 hover:shadow-lg disabled:opacity-70"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={20} />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Check className="mr-2" size={20} />
                      Submit Request
                    </>
                  )}
                </Button>
              )}
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default CostCalculator;